/**
 * CategoryButton Container Component
 *
 * Container component for category button with memoization.
 * Handles memoization and prop passing to View component.
 * @module components/PlayerDetailRight/CategoryButton/CategoryButtonContainer
 */

import { memo, useCallback } from "react";

import CategoryButtonView from "./CategoryButtonView";
import { IRecommendationsCategorySelection } from "@/stores/recommendationsCategorySelection";

/**
 * Props for CategoryButton component
 */
interface CategoryButtonProps {
  readonly item: IRecommendationsCategorySelection;
  readonly isSelected: boolean;
  readonly isDark: boolean;
  readonly isSmallScreen: boolean;
  readonly onPress: (type: string) => void;
}

/**
 * Category button container for recommendation type selection
 * Memoized to prevent unnecessary re-renders when props haven't changed.
 * @param props - Component props
 * @param props.item - Category item containing type and label
 * @param props.isSelected - Whether this category is currently selected
 * @param props.isDark - Whether dark theme is active
 * @param props.isSmallScreen - Whether device has small screen
 * @param props.onPress - Handler called with category type when pressed
 */
const CategoryButton = memo(
  ({
    item,
    isSelected,
    isDark,
    isSmallScreen,
    onPress,
  }: CategoryButtonProps) => {
    const handlePress = useCallback(() => {
      onPress(item.type);
    }, [onPress, item.type]);

    return (
      <CategoryButtonView
        item={item}
        isSelected={isSelected}
        isDark={isDark}
        isSmallScreen={isSmallScreen}
        onPress={handlePress}
      />
    );
  }
);

CategoryButton.displayName = "CategoryButtonContainer";

export default CategoryButton;
